<script setup>
// Auto-extracted chrome for HeizungSolarBremen.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Heizung Solar Bremen" },
    brandTagline: { type: String, default: "Bremen, kombiniert gedacht." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Heizung Solar Bremen");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Bremen, kombiniert gedacht.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;700&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="hsb" :style="`--hsb-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="hsb-head">
                    <div class="hsb-head__inner">
                        <a class="hsb-head__brand" href="/">
                            <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
                                <circle cx="17" cy="17" r="15" fill="none" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M10 22l4-9 3 6 3-4 4 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                <circle cx="24" cy="11" r="3" fill="currentColor"/>
                            </svg>
                            <div>
                                <span class="hsb-head__name">{{ brandName }}</span>
                                <span class="hsb-head__tag">{{ brandTagline }}</span>
                            </div>
                        </a>
                        <a href="#hsb-check" class="hsb-head__cta">Kombi-Check &rarr;</a>
                    </div>
                </header>
        <slot />
        <footer class="hsb-foot">
                    <div class="hsb-foot__inner">
                        <p>{{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav><a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a><a href="/agb">AGB</a><a href="/widerruf">Widerruf</a><a href="/ratgeber">Ratgeber</a></nav>
                        <p class="hsb-foot__copy">&copy; {{ new Date().getFullYear() }} heizung-solar-bremen.de</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.hsb { --hsb-paper: #fff8f1; --hsb-ink: #1c1410; --hsb-sub: #6f5c50; --hsb-line: #ead8c8; --hsb-soft: #fef3e8;
    font-family: 'Inter', system-ui, sans-serif; color: var(--hsb-ink); background: var(--hsb-paper); min-height: 100dvh; line-height: 1.6; -webkit-font-smoothing: antialiased; }
.hsb *, .hsb *::before, .hsb *::after { box-sizing: border-box; }
.hsb h1, .hsb h2, .hsb h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 700; letter-spacing: -0.01em; line-height: 1.05; margin: 0; }
.hsb h1 em, .hsb h2 em { color: var(--hsb-accent); font-style: italic; }

.hsb-head { border-bottom: 1px solid var(--hsb-line); background: var(--hsb-paper); position: sticky; top: 0; z-index: 30; }
.hsb-head__inner { max-width: 1180px; margin: 0 auto; padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.hsb-head__brand { display: inline-flex; align-items: center; gap: 12px; color: var(--hsb-accent); text-decoration: none; }
.hsb-head__brand > div { display: flex; flex-direction: column; }
.hsb-head__name { font-family: 'Cormorant Garamond', serif; font-weight: 700; font-size: 20px; color: var(--hsb-ink); line-height: 1; }
.hsb-head__tag { color: var(--hsb-sub); font-size: 12px; margin-top: 2px; }
.hsb-head__cta { background: var(--hsb-accent); color: #fff; padding: 10px 18px; text-decoration: none; font-size: 13px; font-weight: 600; border-radius: 30px; transition: background 200ms ease; }
.hsb-head__cta:hover { background: #7c2d12; }

.hsb-hero { padding: 64px 24px 48px; }
.hsb-hero__inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 40px; align-items: center; }
@media (min-width: 880px) { .hsb-hero__inner { grid-template-columns: 1.4fr 1fr; gap: 64px; } .hsb-hero { padding: 96px 24px 64px; } }
.hsb-hero__kicker { font-size: 13px; color: var(--hsb-sub); font-variant-numeric: tabular-nums; margin-bottom: 18px; }
.hsb-hero__kicker em { color: var(--hsb-accent); font-style: normal; font-weight: 600; }
.hsb-hero__h { font-size: clamp(40px, 7vw, 80px); margin-bottom: 18px; text-wrap: balance; }
.hsb-hero__sub { color: var(--hsb-sub); font-size: 17px; max-width: 56ch; margin-bottom: 28px; }
.hsb-hero__cta { display: inline-flex; align-items: center; gap: 12px; background: var(--hsb-accent); color: #fff; border: none; padding: 18px 30px; font-size: 17px; font-weight: 600; border-radius: 50px; cursor: pointer; font-family: inherit; transition: all 220ms cubic-bezier(0.32,0.72,0,1); }
.hsb-hero__cta:hover { background: #7c2d12; transform: translateY(-1px); }
.hsb-hero__arr { transition: transform 200ms ease; }
.hsb-hero__cta:hover .hsb-hero__arr { transform: translateX(3px); }
.hsb-hero__meta { font-size: 13px; color: var(--hsb-sub); margin-top: 14px; }

.hsb-hero__map { display: flex; justify-content: center; color: var(--hsb-accent); }
.hsb-map { max-width: 280px; width: 100%; height: auto; }

.hsb-bar { max-width: 1180px; margin: 56px auto 0; display: grid; grid-template-columns: 1fr; gap: 12px; }
@media (min-width: 720px) { .hsb-bar { grid-template-columns: repeat(3, 1fr); gap: 0; border-top: 1px solid var(--hsb-line); padding-top: 24px; } }
.hsb-bar > div { display: flex; align-items: baseline; gap: 14px; padding: 4px 24px 4px 0; }
.hsb-bar strong { font-family: 'Cormorant Garamond', serif; font-size: 36px; color: var(--hsb-accent); font-weight: 700; }
.hsb-bar span { font-size: 14px; color: var(--hsb-sub); }

/* Check */
.hsb-check { padding: 80px 24px; background: var(--hsb-soft); border-top: 1px solid var(--hsb-line); border-bottom: 1px solid var(--hsb-line); }
.hsb-check__inner { max-width: 760px; margin: 0 auto; }
.hsb-check__head { text-align: center; margin-bottom: 32px; }
.hsb-check__chip { display: inline-block; font-size: 11px; color: var(--hsb-accent); text-transform: uppercase; letter-spacing: 0.16em; font-weight: 700; padding: 6px 14px; background: var(--hsb-paper); border-radius: 30px; border: 1px solid var(--hsb-line); margin-bottom: 18px; }
.hsb-check__head h2 { font-size: clamp(32px, 5vw, 48px); margin-bottom: 10px; }
.hsb-check__hint { color: var(--hsb-sub); font-size: 15px; max-width: 50ch; margin: 0 auto; }
.hsb-check__prog { display: flex; justify-content: center; gap: 8px; margin-top: 24px; }
.hsb-check__prog span { width: 24px; height: 4px; background: var(--hsb-line); border-radius: 2px; transition: background 200ms ease; }
.hsb-check__progDot--on { background: var(--hsb-accent) !important; }

.hsb-q { background: var(--hsb-paper); border: 1px solid var(--hsb-line); border-radius: 12px; padding: 32px; }
.hsb-plz { width: 100%; padding: 24px 20px; font-size: 32px; text-align: center; font-family: 'Cormorant Garamond', serif; font-weight: 700; letter-spacing: 0.1em; border: 1px solid var(--hsb-line); border-radius: 8px; background: var(--hsb-soft); color: var(--hsb-ink); }
.hsb-plz:focus { outline: none; border-color: var(--hsb-accent); }
.hsb-plz__hint { text-align: center; margin-top: 14px; font-size: 14px; }
.hsb-plz__hint--ok { color: #15803d; }
.hsb-plz__hint--warn { color: #92400e; }

.hsb-slider input[type=range] { width: 100%; accent-color: var(--hsb-accent); }
.hsb-slider__val { text-align: center; font-family: 'Cormorant Garamond', serif; font-size: 48px; color: var(--hsb-accent); font-weight: 700; line-height: 1; margin-top: 16px; }
.hsb-slider__val em { font-style: normal; font-size: 0.4em; color: var(--hsb-sub); margin-left: 8px; font-family: 'Inter', sans-serif; font-weight: 500; }
.hsb-slider__hint { text-align: center; color: var(--hsb-sub); font-size: 13px; margin-top: 8px; }

.hsb-opts { display: grid; grid-template-columns: 1fr; gap: 10px; }
.hsb-opt { display: flex; align-items: center; gap: 14px; padding: 18px 22px; background: var(--hsb-soft); border: 1px solid var(--hsb-line); border-radius: 8px; cursor: pointer; font-family: inherit; font-size: 16px; text-align: left; transition: all 180ms ease; color: var(--hsb-ink); }
.hsb-opt:hover { border-color: var(--hsb-accent); }
.hsb-opt--on { background: var(--hsb-accent); color: #fff; border-color: var(--hsb-accent); }
.hsb-opt__pip { width: 14px; height: 14px; border-radius: 50%; border: 2px solid currentColor; position: relative; flex-shrink: 0; }
.hsb-opt--on .hsb-opt__pip::after { content: ''; position: absolute; inset: 2px; background: #fff; border-radius: 50%; }

.hsb-q__nav { display: flex; justify-content: space-between; margin-top: 24px; gap: 12px; }
.hsb-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 22px; border-radius: 30px; border: 1px solid transparent; cursor: pointer; font-family: inherit; font-weight: 600; font-size: 14px; transition: all 200ms ease; text-decoration: none; }
.hsb-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.hsb-btn--primary { background: var(--hsb-accent); color: #fff; }
.hsb-btn--primary:hover:not(:disabled) { background: #7c2d12; }
.hsb-btn--ghost { background: transparent; color: var(--hsb-sub); border-color: var(--hsb-line); }
.hsb-btn--ghost:hover { color: var(--hsb-ink); border-color: var(--hsb-ink); }
.hsb-btn--lg { padding: 16px 28px; font-size: 16px; }
.hsb-btn--sm { padding: 8px 14px; font-size: 13px; }
.hsb-btn--full { width: 100%; }

/* Result */
.hsb-tier { display: inline-flex; align-items: center; gap: 10px; padding: 10px 18px; border-radius: 30px; font-size: 14px; font-weight: 600; margin-bottom: 24px; }
.hsb-tier--3 { background: #ecfccb; color: #365314; }
.hsb-tier--2 { background: #fef3c7; color: #78350f; }
.hsb-tier--1 { background: #fee2e2; color: #991b1b; }
.hsb-tier__dot { width: 10px; height: 10px; border-radius: 50%; background: currentColor; }
.hsb-result__h { font-family: 'Cormorant Garamond', serif; font-size: 56px; line-height: 1; margin-bottom: 12px; font-weight: 700; }
.hsb-result__h em { color: var(--hsb-accent); font-style: italic; }
.hsb-result__sub { color: var(--hsb-sub); font-size: 16px; max-width: 56ch; margin-bottom: 32px; }
.hsb-foerder { background: var(--hsb-paper); border: 1px solid var(--hsb-line); border-radius: 10px; padding: 24px; margin-bottom: 32px; }
.hsb-foerder__chip { font-size: 11px; color: var(--hsb-accent); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 700; margin-bottom: 14px; }
.hsb-foerder ul { margin: 0; padding-left: 20px; display: grid; gap: 8px; }
.hsb-foerder li { font-size: 14px; color: var(--hsb-ink); }
.hsb-foerder strong { color: var(--hsb-accent); }

.hsb-lead { background: var(--hsb-paper); border: 1px solid var(--hsb-line); border-radius: 12px; padding: 32px; }
.hsb-lead h3 { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 6px; }
.hsb-lead > p { color: var(--hsb-sub); margin-bottom: 20px; font-size: 15px; }
.hsb-form { display: grid; gap: 14px; }
.hsb-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.hsb-input { display: flex; flex-direction: column; gap: 6px; }
.hsb-input > span { font-size: 13px; font-weight: 600; }
.hsb-input em { font-weight: 400; color: var(--hsb-sub); font-style: normal; }
.hsb-input input { padding: 14px 16px; font-size: 16px; border: 1px solid var(--hsb-line); border-radius: 6px; background: var(--hsb-soft); font-family: inherit; color: var(--hsb-ink); }
.hsb-input input:focus { outline: none; border-color: var(--hsb-accent); }
.hsb-input--err input { border-color: #b91c1c; }
.hsb-consent { display: flex; gap: 12px; padding: 14px; background: var(--hsb-soft); border: 1px solid var(--hsb-line); border-radius: 6px; font-size: 13px; color: var(--hsb-sub); line-height: 1.5; cursor: pointer; }
.hsb-consent input { margin-top: 2px; flex-shrink: 0; accent-color: var(--hsb-accent); }
.hsb-err { color: #b91c1c; font-size: 13px; text-align: center; }

.hsb-done { text-align: center; padding: 32px; background: var(--hsb-paper); border: 1px solid var(--hsb-line); border-radius: 12px; }
.hsb-done__big { font-family: 'Cormorant Garamond', serif; font-size: 36px; margin-bottom: 12px; font-weight: 700; }

.hsb-alt { background: var(--hsb-paper); border: 1px solid var(--hsb-line); border-radius: 12px; padding: 28px; }
.hsb-alt h3 { font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.hsb-alt > p { color: var(--hsb-sub); margin-bottom: 16px; }
.hsb-alt ul { margin: 0 0 20px; padding-left: 20px; color: var(--hsb-sub); display: grid; gap: 6px; }

.hsb-meth { padding: 80px 24px; background: var(--hsb-paper); }
.hsb-meth__inner { max-width: 980px; margin: 0 auto; }
.hsb-meth__chip { color: var(--hsb-accent); text-transform: uppercase; font-size: 11px; letter-spacing: 0.16em; font-weight: 700; margin-bottom: 14px; }
.hsb-meth h2 { font-size: clamp(34px, 5vw, 52px); margin-bottom: 44px; max-width: 22ch; }
.hsb-meth__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 32px; }
.hsb-meth__list li { display: grid; grid-template-columns: auto 1fr; gap: 24px; padding-bottom: 32px; border-bottom: 1px solid var(--hsb-line); align-items: start; }
.hsb-meth__list li:last-child { border-bottom: 0; }
.hsb-meth__no { font-family: 'Cormorant Garamond', serif; font-size: 56px; color: var(--hsb-accent); line-height: 1; font-weight: 700; }
.hsb-meth__list h3 { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 8px; }
.hsb-meth__list p { color: var(--hsb-sub); font-size: 15px; max-width: 56ch; }

.hsb-faq { padding: 80px 24px; background: var(--hsb-soft); border-top: 1px solid var(--hsb-line); }
.hsb-faq__inner { max-width: 900px; margin: 0 auto; }
.hsb-faq h2 { font-size: clamp(28px, 4vw, 42px); margin-bottom: 32px; }
.hsb-faq dl { display: grid; gap: 28px; }
.hsb-faq dt { font-weight: 700; font-family: 'Inter', sans-serif; margin-bottom: 6px; font-size: 16px; }
.hsb-faq dd { color: var(--hsb-sub); margin: 0; font-size: 15px; }

.hsb-foot { background: var(--hsb-ink); color: #d4c8bc; padding: 32px 24px; }
.hsb-foot__inner { max-width: 1180px; margin: 0 auto; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; }
.hsb-foot nav { display: flex; gap: 18px; }
.hsb-foot a { color: #f5e9da; text-decoration: none; }
.hsb-foot a:hover { color: #fff; }
.hsb-foot__copy { font-size: 12px; opacity: 0.7; }

.hsb-stick { position: fixed; left: 0; right: 0; bottom: 0; padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); background: var(--hsb-accent); color: #fff; display: flex; justify-content: space-between; align-items: center; gap: 16px; z-index: 40; font-size: 14px; }
.hsb-stick .hsb-btn { background: #fff; color: var(--hsb-accent); }
.hsb-stick .hsb-btn:hover { background: #fff8f1; }
@media (min-width: 880px) { .hsb-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .hsb *, .hsb *::before, .hsb *::after { transition: none !important; animation: none !important; transform: none !important; }
}

/* ── Trust strip (images + cites + byline) — hsb-* unique treatment ── */
.hsb-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.hsb-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .hsb-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.hsb-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid rgba(20,17,13,0.10); padding: 6px; background: #fff; box-shadow: 0 14px 32px -18px #9A341255;
}
.hsb-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.hsb-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.hsb-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.hsb-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.hsb-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.hsb-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #9A341255;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.hsb-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hsb-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #9A3412;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.hsb-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.hsb-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.hsb-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.hsb-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.hsb-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.hsb-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
